/**
 * Top-zone scaffold curation (s99, Rule A).
 *
 * The two-zone expanded section (`ExpandableSectionLayout`) splits scaffold rows
 * into the TOP zone ("Fill these in") vs the SUGGESTION tray. Beyond the structural
 * rules (locked / user-data / essential-tied / promoted), this map curates WHICH
 * untouched scaffolds belong in the top zone per section — the common cases a
 * planner almost always fills (e.g. Chequing + Savings), leaving the long tail in
 * the tray.
 *
 * Keyed by `SECTION_SLUGS`. Values are matched against the row's CURATION field —
 * the section's first basic field (the "type"/"category" key, e.g. account_type,
 * doc_type, role) — NOT the data-preview field (which may be a name; D-99 decision
 * 1 keeps the two lookups separate). Match is `rowValue.includes(item)`,
 * case-insensitive, so an item is authored as a substring of (or equal to) the
 * actual seeded value: "Property" matches "…for Property (CPOA)", "OAS" matches
 * "OAS / GIS". Sections absent from this map curate nothing — their untouched
 * scaffolds all fall to the suggestion tray (the default).
 *
 * Under shared/constants/ (excluded from `check:slugs`); references SECTION_SLUGS.
 */
import { SECTION_SLUGS } from "./sections";

export const TOP_ZONE_SCAFFOLDS: Record<string, readonly string[]> = {
  // ── Legal ──────────────────────────────────────────────────────────────────
  [SECTION_SLUGS.LEGAL_YOUR_EXECUTOR]: ["Primary", "Alternate"],
  [SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS]: [
    "Will & Testament",
    "Healthcare Directive", // matches "Healthcare Directive / Living Will"
    // s101: Birth Certificate / Marriage Certificate / Property Deed dropped from the
    // shaded set (Birth Cert moves to Gov IDs in the deferred pass).
  ],
  [SECTION_SLUGS.LEGAL_POWERS_OF_ATTORNEY]: ["Personal Care", "Property"],
  [SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS]: ["Estate Lawyer", "Accountant", "Financial Advisor"],

  // ── Financial & Insurance ────────────────────────────────────────────────────
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]: ["Chequing", "Savings"],
  [SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP]: ["RRSP", "TFSA", "Non-Registered"],
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]: [
    "Life Insurance",
    "Home / Property Insurance",
    "Auto / Vehicle Insurance",
  ],
  // s101: Income shades only Employment (EXACT match — see TOP_ZONE_EXACT_MATCH — so
  // it never shades a "Self-Employment" row). Until the Employment default row is
  // seeded (deferred pass), no row matches and shaded mode falls through to engaged.
  [SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS]: ["Employment"],
  // s101: shade only Mortgage + Credit Card; Line of Credit / Car Loan fall to plain rows.
  [SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES]: ["Mortgage", "Credit Card"],

  // ── Assets ───────────────────────────────────────────────────────────────────
  [SECTION_SLUGS.ASSETS_PROPERTIES]: ["Primary Residence"],
  // s101: Valuables shaded set stripped — "Safety Deposit Box" implied everyone has one
  // (false). Valuables is a T3 add-what-you-have list → no shaded rows, engaged/legacy.
  // Vehicle List has no useful "type" to curate on — the generic starter row carries
  // a NAME ("Your vehicle") in make_model, matched via TOP_ZONE_CURATION_FIELD below.
  [SECTION_SLUGS.ASSETS_VEHICLE_LIST]: ["Your vehicle"],

  // ── Personal Information ─────────────────────────────────────────────────────
  [SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS]: [
    "Driver's License", // actual seeded spelling ("License", not "Licence")
    "Health Card",
    "Passport",
    "Social Insurance Number", // matches "Social Insurance Number (SIN)"
  ],
  // Employment History has no "type" field AND its starter row ships BLANK (s142,
  // FIELDFLICKER-1 Fix 4 — the seeded "Current employer" value was a scaffold that read as
  // real data). It can't be curated on a value, so its seeded starter is curated by
  // ROW IDENTITY instead — see TOP_ZONE_SEEDED_STARTER_SECTIONS below.
  // s106 (MIG-1 / DRA-1): Health Insurance shades its two starter types. Ribbon leads
  // with `provider` (blank on a fresh row), so curation reads `plan_type` — the
  // TOP_ZONE_CURATION_FIELD override below (same pattern as Bank's account_type).
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE]: ["Extended Health", "Dental"],
  // DB-9: Pets curate on `pet_name` (its first basic field, no override). The seeded
  // starter row carries the placeholder name "Your pet" (mirrors Vehicle "Your vehicle").
  [SECTION_SLUGS.FAMILY_PETS]: ["Your pet"],

  // ── Instructions for Executors ───────────────────────────────────────────────
  // s101: Funeral curates on `topic` (its first basic field). Funeral Home + Religion
  // are the two facts most planners want recorded first; the rest fall to plain rows.
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: ["Funeral Home", "Religion / Faith"],
};

/**
 * Per-section override for the field the curation matches against. Default is the
 * section's first basic field (the "type" key). A few sections have no meaningful
 * type to curate on, so their generic starter row is identified by a NAME field
 * (Vehicle → make_model, Employment → employer) — decision: match on that instead.
 */
export const TOP_ZONE_CURATION_FIELD: Record<string, string> = {
  [SECTION_SLUGS.ASSETS_VEHICLE_LIST]: "make_model",
  // Career removed (s142, Fix 4): its starter is curated by row identity, not a value —
  // see TOP_ZONE_SEEDED_STARTER_SECTIONS.
  // s101 (#1): Bank's ribbon now leads with `bank` (the identity), but the shaded set
  // (Chequing/Savings) is keyed on the TYPE — so curate on account_type, not the
  // blank first basic field, or the shading would break.
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]: "account_type",
  // s106 (MIG-1 / DRA-1): Health Insurance leads with `provider` (blank on the starter),
  // so curate on the type field `plan_type` (Extended Health / Dental).
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE]: "plan_type",
};

/**
 * s101 (#9): sections whose curation value must match a shaded item by EXACT
 * (case-insensitive) equality rather than substring. Needed where the default
 * substring match would over-match — e.g. Income's "Employment" must NOT shade a
 * user's "Self-Employment" row. Sections absent here keep the substring default.
 */
export const TOP_ZONE_EXACT_MATCH: ReadonlySet<string> = new Set([
  SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS,
]);

/**
 * s142 (FIELDFLICKER-1 Fix 4): sections whose single generic starter row has NO curation
 * "type" field AND ships BLANK — so it can't be matched on a value. Its seeded starter row
 * is curated into the top zone by ROW IDENTITY (isSeededDefaultRow) instead, value-independent.
 * A seeded starter therefore floats to the top zone whether it is blank or a planner has
 * filled it with a real value. Only Career qualifies today (Vehicle/Pets still carry a
 * placeholder NAME value and stay value-matched). Callers combine this with isSeededDefaultRow;
 * every OTHER section still resolves purely through isTopZoneScaffold (value match), unchanged.
 */
export const TOP_ZONE_SEEDED_STARTER_SECTIONS: ReadonlySet<string> = new Set([
  SECTION_SLUGS.PERSONAL_INFORMATION_CAREER,
  // s154 round 3 (item 1, D-154.25 real fix): the password/portal sections are pure
  // "accounts you might have" scaffold lists — every seeded row (CRA My Account, a bank portal,
  // a social account…) is a prompt to fill OR decline, never to delete. They were in NEITHER
  // TOP_ZONE_SCAFFOLDS nor here, so their seeded rows rendered PLAIN (delete trash) and never
  // reached the shaded branch that carries the row-level "I don't have this" affordance — which is
  // why the round-2 rowHasUserData fix (correct, but in the shaded branch) never took on gov-tax.
  // Curating them by IDENTITY (value-independent) is robust to the s154 site_app option collapse +
  // orphan-remap that broke any value-based match. User-added rows keep a UUID → stay plain/deletable.
  SECTION_SLUGS.PASSWORD_LOG_DEVICES_LOCAL_ACCESS,
  SECTION_SLUGS.PASSWORD_LOG_PRIMARY_EMAIL_IDENTITY_ACCOUNTS,
  SECTION_SLUGS.PASSWORD_LOG_GOVERNMENT_TAX,
  SECTION_SLUGS.PASSWORD_LOG_ONLINE_ACCOUNTS_COMMUNITIES,
  SECTION_SLUGS.PASSWORD_LOG_DIGITAL_FILES_CLOUD_STORAGE,
]);

/** The field key the top-zone curation should read for `sectionSlug` — the override
 *  if present, else the supplied default (the caller's first basic field). */
export function topZoneCurationFieldKey(
  sectionSlug: string,
  defaultKey: string | undefined,
): string | undefined {
  return TOP_ZONE_CURATION_FIELD[sectionSlug] ?? defaultKey;
}

/** True when `curationValue` (the row's first-basic-field value) marks the row as a
 *  curated top-zone scaffold for `sectionSlug`. Case-insensitive substring match. */
export function isTopZoneScaffold(
  sectionSlug: string,
  curationValue: string | null | undefined,
): boolean {
  const list = TOP_ZONE_SCAFFOLDS[sectionSlug];
  if (!list) return false;
  const v = (curationValue ?? "").trim().toLowerCase();
  if (!v) return false;
  const exact = TOP_ZONE_EXACT_MATCH.has(sectionSlug);
  return list.some((item) => {
    const it = item.toLowerCase();
    return exact ? v === it : v.includes(it);
  });
}
